import axios, { CancelTokenSource } from 'axios';
import { FormApi, FormState } from 'final-form';
import React, { Component, Fragment } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import styled from 'styled-components';
import {
  BillsMergingInfo,
  CountryDto,
  LanguageDto,
  PatientDuplicateResponse,
  PatientDuplicateResponseInfo,
  PatientMergeRequest,
  PatientMergeResponse,
  PatientMergingInfo,
  PatientMergingSearchRequest,
  PatientMergingSearchResponse,
  TreatmentMergingInfo,
  UpsertPatientDto,
  UpsertTreatmentDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { isKeyCheck } from 'utils/keyUtils';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import ReactHotkeys from 'react-hot-keys';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Button, Dimmer, Grid, Icon, Loader, Message, Modal } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import { CheckboxSc } from 'components/final-form/CheckBox';
import moment from 'moment';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import {
  copyInsuranceFromMaster,
  mergePatients,
  searchDuplicatePatient,
  searchPatientMerging
} from 'services/patientMergingServices';
import { BillStatus, TestResultStatus } from 'ts-types/api.enums';
import { getBillIcon } from 'components/bill/utils/billUtils';
import { isoToDisplayDate, isoToDisplayDateTime } from 'utils/dateUtils';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import DuplicatePatientsListModal from 'components/billing/patient-merging/DuplicatePatientsListModal';
import { getTreatmentById } from 'services/treatmentServices';
import TreatmentsForm from 'components/treatments/TreatmentsForm';
import {
  ConsultationsConsumerRenderProps,
  withConsultationsContext
} from 'components/consultations/ConsultationsContext';
import UpsertPatient from 'components/patient/upsert/UpsertPatient';
import { getAllCountries } from 'services/countryServices';
import { getAllLanguages } from 'services/languageServices';
import { findPatientById } from 'services/patientServices';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import { debounce } from 'ts-debounce';
import SearchResultComponent from 'components/SearchResultComponent';
import { numberFormatter } from 'utils/fomattingUtils';
import { TreatmentMode } from 'components/treatments/TreatmentsContext';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import PxGrid from 'components/layout/PxGrid';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';

const StyledCell = styled.div`
  text-align: right;
  padding-right: 1rem;
`;

const StyledBillCell = styled.div`
  text-align: right;
  padding-right: 6rem;
`;

const StyleTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__rowColumn:nth-child(10) {
    border-right: none;
    white-space: pre-wrap;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
    white-space: pre-wrap;
  }
`;

const StyledModal = styled(Modal)`
  width: 600px !important;
  margin: 0;
`

const StyledTableGrid = styled(Grid)`
  padding-left: 0;

  .edit-treatment-icon {
    padding-left: 8px;
  }
`

const TableDiv = styled.div`
  flex: 1 0 auto;
  padding: 0.5rem;
  background: white;
  min-height: 270px;
  max-height: 270px;
`;

const FormDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex: 0 0 auto;
`;

const SearchFormLeftDiv = styled.div`
  flex: 1 0 auto;
`;

const TableWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: 481px;
  max-height: 481px;
  padding-bottom: 1.5rem;

  .patients-search-container {
    margin-top: 1rem;
    flex: 1 1 auto;
  }

  .edit-patient-icon {
    padding-left: 8px;
  }
`;

const StyledButtonRow = styled(Grid.Row)`
  margin-bottom: 1rem; !important;
`

const rowStyle = {
  cursor: "pointer",
  userSelect: "none"
};

const selectedRow = {
  ...rowStyle,
  backgroundColor: "#e0e0e0"
};

const CheckboxDiv = styled.div`
  display: flex;
  flex-direction: row;

  .checkbox {
    margin-right: 1.5rem;
  }
`;

const StyledLabel = styled.div`
  margin-bottom: .8rem;
`;

const initialPatientSearch: Partial<PatientMergingSearchRequest> = {
  includeInactive: true
}

interface PatientFieldProps extends FormState {
  values: Partial<PatientMergingSearchRequest>
}

export interface EditTreatmentValues extends UpsertTreatmentDto {
  id: number
}

interface Props extends MessageSourceProps, ConsultationsConsumerRenderProps, AuthConsumerRenderProps,
    MandateConsumerRenderProps, NotificationSystemProps {
  onPatientSelected: (patient?: PatientMergingSearchResponse) => void
}

interface State {
  foundPatientMergingInfos?: PatientMergingSearchResponse,
  duplicatePatients?: PatientDuplicateResponseInfo,
  selectedDuplicatePatientIx?: number,
  selectedPatientIx?: number,
  searchValues: Partial<PatientMergingSearchRequest>,
  selectedPatient?: PatientMergingInfo,
  selectedTreatment?: TreatmentMergingInfo,
  patientsLoaded: boolean,
  masterPatientId?: number,
  masterMergeFlag: boolean,
  masterPatientContainer?: String,
  addEditModalOpen: boolean,
  editTreatment?: EditTreatmentValues,
  patientId?: number,
  patient?: UpsertPatientDto,
  countries: Array<CountryDto>,
  languages: Array<LanguageDto>,
  editTreatmentModalOpen: boolean,
  editPatientModalOpen: boolean,
  mergeResponse?: Partial<PatientMergeResponse>,
  mergeSuccess: boolean,
  cancelTokenSource: CancelTokenSource
}

class PatientMergingView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    getAllCountries(cancelTokenSource).then(response => {
      this.setState({
        countries: response
      });
    }).catch(noop);

    getAllLanguages(cancelTokenSource).then(response => {
      this.setState({
        languages: response
      });
    }).catch(noop);

    this.state = {
      patientsLoaded: true,
      searchValues: initialPatientSearch,
      addEditModalOpen: false,
      editTreatmentModalOpen: false,
      editPatientModalOpen: false,
      countries: [],
      languages: [],
      masterMergeFlag: false,
      mergeSuccess: false,
      cancelTokenSource
    };
  }

  resetSearchData = () => {
    this.setState({
      foundPatientMergingInfos: undefined,
      selectedPatient: undefined,
      selectedTreatment: undefined,
      masterMergeFlag: false
    });
  };

  markPatient = (patientIndex: number) => {

    const {foundPatientMergingInfos} = this.state;
    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {
      this.setState({
        selectedPatient: foundPatientMergingInfos.patientMergeData[patientIndex]
      });
    }
  };

  resetSearchPatients = (form: FormApi) => {
    if (!this.mounted) {
      return;
    }

    form.reset({
      includeInactive: true
    });

    this.setState({
      foundPatientMergingInfos: undefined,
      masterPatientId: undefined,
      masterPatientContainer: undefined
    })

  };

  mergePatientsAction = (values?: Partial<PatientMergingSearchRequest>) => {
    const {foundPatientMergingInfos, masterPatientId} = this.state;

    const onFinally = () => {
      this.setState({
        patientsLoaded: true,
        mergeSuccess: true
      })
    };

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData.length && masterPatientId) {
      const mergingPatientIds = foundPatientMergingInfos.patientMergeData
          .filter(p => p.mergeSelection)
          .map(p => p.patientId);

      const request: PatientMergeRequest = {masterPatientId: masterPatientId, mergingPatientIds: mergingPatientIds};

      mergePatients(request, this.state.cancelTokenSource)
          .then(response => {
            this.setState({
              mergeResponse: response
            })
            if (!response.generalError && (!response.errors || response.errors.length == 0)) {
              // no error do the search request again:
              this.showMergeResponseMessage(response);
              this.patientMergingSearch(values);
            } else if (response.generalError || (response.errors && response.errors.length > 0)) {
              this.showMergeResponseMessage(response);
            }
          })
          .catch(noop)
          .finally(onFinally);
    }

    this.setState({patientsLoaded: true});
  };

  copyInsuranceAction = (values?: Partial<PatientMergingSearchRequest>) => {
    const {foundPatientMergingInfos, masterPatientId} = this.state;

    const onFinally = () => {
      this.setState({
        patientsLoaded: true,
        mergeSuccess: true
      })
    };

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData.length && masterPatientId) {
      const mergingPatientIds = foundPatientMergingInfos.patientMergeData
          .filter(p => p.mergeSelection)
          .map(p => p.patientId);

      const request: PatientMergeRequest = {masterPatientId: masterPatientId, mergingPatientIds: mergingPatientIds};

      copyInsuranceFromMaster(request, this.state.cancelTokenSource)
          .then(response => {
            this.setState({
              mergeResponse: response
            })
            if (!response.generalError && (!response.errors || response.errors.length == 0)) {
              // no error do the search request again:
              this.showMergeResponseMessage(response);
              this.patientMergingSearch(values);
            } else if (response.generalError || (response.errors && response.errors.length > 0)) {
              this.showMergeResponseMessage(response);
            }
          })
          .catch(noop)
          .finally(onFinally);
    }

    this.setState({patientsLoaded: true});
  };

  patientMergingSearch = (values?: Partial<PatientMergingSearchRequest>) => {
    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        patientsLoaded: true
      })
    };

    this.setState({
      patientsLoaded: false,
      masterPatientId: undefined,
      masterPatientContainer: undefined,
      foundPatientMergingInfos: undefined
    });

    if (values) {
      const searchRequest: Partial<PatientMergingSearchRequest> = {
        ...values
      };

      searchPatientMerging(cancelTokenSource, searchRequest).then((patientsMergingInfo) => {
            this.setState({
              foundPatientMergingInfos: patientsMergingInfo,
              selectedPatientIx: patientsMergingInfo.patientMergeData.length > 0 ? 0 : undefined,
              selectedPatient: patientsMergingInfo.patientMergeData.length > 0 ? patientsMergingInfo.patientMergeData[0] : undefined,
              selectedTreatment: patientsMergingInfo.patientMergeData.length > 0 ? patientsMergingInfo.patientMergeData[0].treatments[0] : undefined
            });
          })
          .catch(noop)
          .finally(onFinally);
    }
    this.setState({patientsLoaded: true});
  };

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  onSelectPatientRow(foundPatient: any, index: any) {
    this.setState({
      selectedPatient: foundPatient,
      selectedPatientIx: index
    });
  };

  changeSelectedPatient(event: any, source?: string) {
    event.persist();
    const {selectedPatientIx, foundPatientMergingInfos} = this.state;

    const isKey: any = isKeyCheck(event);

    if (selectedPatientIx !== undefined && foundPatientMergingInfos) {

      const patientMergingDatas = foundPatientMergingInfos.patientMergeData;

      const goDown = patientMergingDatas.length > 0 && (selectedPatientIx + 1 <= patientMergingDatas.length - 1);
      const goUp = patientMergingDatas.length > 0 && (selectedPatientIx - 1 >= 0);

      if (goDown && isKey.arrowDown) {
        const index = selectedPatientIx + 1;
        this.onSelectPatientRow(patientMergingDatas[index], index);
      } else if (goUp && isKey.arrowUp) {
        const index = selectedPatientIx - 1;
        this.onSelectPatientRow(patientMergingDatas[index], index);
      }

      const tableContainer = document.getElementById('patient-search-table-container');
      const focusedTable = tableContainer ? tableContainer
          .querySelector('.ReactVirtualized__Grid.ReactVirtualized__Table__Grid') : null;

      if (tableContainer && (isKey.pageUp || isKey.pageDown)) {
        tableContainer.focus();
      }

      const activeElement = document.activeElement === tableContainer;

      if (isKey.arrowUp || isKey.pageUp || isKey.pageDown) {
        event.preventDefault();
      }

      if (isKey.pageUp && focusedTable && activeElement) {
        //@ts-ignore
        const height = focusedTable.offsetHeight;

        if (selectedPatientIx !== undefined) {
          const jumpTo = selectedPatientIx - Math.floor(height / 25);

          if (jumpTo < 0) {
            this.onSelectPatientRow(patientMergingDatas[0], 0);
          } else {
            this.onSelectPatientRow(patientMergingDatas[jumpTo], jumpTo);
          }
        }
      }

      if (isKey.pageDown && focusedTable && activeElement) {
        //@ts-ignore
        const height = focusedTable.offsetHeight;

        if (selectedPatientIx !== undefined) {

          const jumpTo = Math.floor(height / 25) + selectedPatientIx;

          if (jumpTo > patientMergingDatas.length - 1) {
            this.onSelectPatientRow(patientMergingDatas[patientMergingDatas.length - 1], patientMergingDatas.length - 1);
          } else {
            this.onSelectPatientRow(patientMergingDatas[jumpTo], jumpTo);
          }
        }
      }

      if (source) {
        const searchElementId = source === 'search' ? "patient-search-input" : "patient-first-name-search-input";
        const searchInputElement = document.getElementById(searchElementId)
        if (searchInputElement) {
          searchInputElement.focus();
        }
      }

    }

  }

  handleChange = debounce(({values}: PatientFieldProps): void => {
    if (!this.mounted) {
      return;
    }


    this.setState({patientsLoaded: false});

    this.patientMergingSearch(values);


  }, 300);


  showModal = () => {

    const {cancelTokenSource} = this.state;

    searchDuplicatePatient(cancelTokenSource).then(response => {
      this.setState({
        duplicatePatients: response,
        addEditModalOpen: true
      })
    })

  }

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false
    }))
  }

  openEditTreatmentModal = (treatmentId: number, mode?: TreatmentMode) => {
    const {cancelTokenSource} = this.state;
    if (treatmentId) {

      getTreatmentById(treatmentId, cancelTokenSource)
          .then(response => {
            this.setState({
              editTreatment: {
                ...response,
                id: treatmentId,
                costEstimationId: response.costEstimationId ? response.costEstimationId : -1,
                readOnly: mode === TreatmentMode.DETAILS
              },
              patientId: response.patientId
            })
            this.toggleEditTreatmentModal();
          })
          .catch(noop)
    }
  }

  toggleEditTreatmentModal = () => {
    this.setState(({editTreatmentModalOpen}: State) => ({
      editTreatmentModalOpen: !editTreatmentModalOpen
    }));
  }

  openEditPatientModal = (patientId?: number) => {
    const {cancelTokenSource} = this.state;

    if (patientId) {
      findPatientById(cancelTokenSource, patientId)
          .then(response => {
            this.setState({
              patient: response,
              patientId: patientId
            })
            this.toggleEditPatientModal();
          })
          .catch(noop)
    }
  };

  onPatientSave = () => {
    this.toggleEditPatientModal();
  };

  toggleEditPatientModal = () => {
    this.setState(({editPatientModalOpen}: State) => ({
      editPatientModalOpen: !editPatientModalOpen
    }));
  }

  onModalClosing = (selectedPatient: PatientDuplicateResponse, form: FormApi) => {

    this.resetSearchPatients(form);

    const birthDateMoment = moment(selectedPatient.birthDate);

    const searchRequest: Partial<PatientMergingSearchRequest> = {
      name: selectedPatient.name,
      firstName: selectedPatient.firstName,
      birthDate: birthDateMoment.isValid() ? birthDateMoment.format("DD.MM.YYYY") : "",
      includeInactive: true
    }

    form.reset(searchRequest);

    this.setState({
      patientsLoaded: false
    })
    this.patientMergingSearch(searchRequest);

    this.hideModal();
  }

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.patientMergingSearch(values)
    }
  };


  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("patient.loading")}</Loader>
        </Dimmer>
    );
  };

  render() {

    const {currentConsultations, currentMandates, getAllMandates} = this.props;
    const {
      patientsLoaded,
      editTreatmentModalOpen,
      editPatientModalOpen,
      editTreatment,
      patientId,
      countries,
      languages,
      patient
    } = this.state;

    return (
        <PraxStyledSegment>

          <div>
            {this.renderPatientSearchSegment()}
          </div>

          <TableWrapperDiv>
            {!patientsLoaded ? this.renderLoader() : this.renderPatientSegment()}
          </TableWrapperDiv>
          <StyledTableGrid>
            <TableDiv key="treatments">
              {!patientsLoaded ? this.renderLoader() : this.renderTreatmentSegment()}
            </TableDiv>
            <TableDiv key="bills">
              {!patientsLoaded ? this.renderLoader() : this.renderBillsSegment()}
            </TableDiv>
          </StyledTableGrid>

          <StyledTableGrid>
            <TableDiv key="vaccination">
              {!patientsLoaded ? this.renderLoader() : this.renderVaccinationSegment()}
            </TableDiv>
            <TableDiv key="testResult">
              {!patientsLoaded ? this.renderLoader() : this.renderTestResultSegment()}
            </TableDiv>
          </StyledTableGrid>

          {
            editPatientModalOpen &&
            <UpsertPatient
                modalOpen={editPatientModalOpen}
                toggleModal={this.toggleEditPatientModal}
                countries={countries}
                languages={languages}
                patient={patient}
                patientId={patientId}
                onPatientSave={this.onPatientSave}
                currentMandates={currentMandates}
                getAllMandates={getAllMandates}

            />
          }
          {
            editTreatmentModalOpen &&
            <TreatmentsForm
                treatmentFormOpen={editTreatmentModalOpen}
                toggleUpsertModal={this.toggleEditTreatmentModal}
                treatment={editTreatment}
                currentPatientId={patientId}
                currentConsultations={currentConsultations}
            />
          }

        </PraxStyledSegment>
    );
  }

  renderPatientSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              includeInactive: true
            }}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  onClickSearch = (values: any, form: FormApi) => {
    const newValue = !values.includeInactive;
    form.change("includeInactive", newValue);
    const searchRequest = {
      ...values,
      includeInactive: newValue
    }
    this.patientMergingSearch(searchRequest);
  }

  renderSearchForm = ({values, form, submitting}: FormRenderProps) => {
    const {masterPatientId, foundPatientMergingInfos} = this.state;

    const {getMessage, currentUser} = this.props;

    const currentUserRoles = currentUser ? currentUser.roles : [];
    const isAppAdmin = currentUserRoles.includes("ROLE_APP_ADMIN");
    const isMandateAdmin = currentUserRoles.includes("ROLE_MANDATE_ADMIN");

    let copyInsurancesDisabled = true;
    let mergeDisabled = true;
    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {
      const mergePatients = foundPatientMergingInfos.patientMergeData.filter(patient => patient.mergeSelection);
      if (masterPatientId && mergePatients.length) {
        copyInsurancesDisabled = false;
      }
    }
    if (isAppAdmin || isMandateAdmin) {
      mergeDisabled = copyInsurancesDisabled;
    }

    let numberOfPatients = 0;
    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData.length > 0) {
      numberOfPatients = foundPatientMergingInfos.patientMergeData.length;
    }

    return (
        <React.Fragment>
          <FormDiv>
            <ReactHotkeys
                keyName="alt+n,option+n"
                filter={() => true}
            >
              <SearchFormLeftDiv>
                <PraxFormGrid onKeyDown={(e: any) => this.onEnterKeyPressed(e, values)}>
                  <React.Fragment>
                    <Grid.Row>
                      <Grid.Column width={3}>
                        <StyledLabel>{getMessage("patient.name")}</StyledLabel>
                        <Field
                            name="name"
                            component={Input}
                            fluid
                            autoFocus
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                      <Grid.Column width={3}>
                        <StyledLabel>{getMessage("patient.firstName")}</StyledLabel>
                        <Field
                            name="firstName"
                            component={Input}
                            fluid
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                      <Grid.Column width={2}>
                        <StyledLabel>{getMessage("patient.birthDate")}</StyledLabel>
                        <Field
                            name="birthDate"
                            component={Input}
                            fluid
                            onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <StyledButtonRow>
                      <Grid.Column width={3}>
                        <CheckboxDiv>
                          <CheckboxSc
                              label={getMessage('patient.search.checkbox.inactive')}
                              checked={(!!values.includeInactive)}
                              onChange={() => this.onClickSearch(values, form)
                              }
                              toggle={true}
                          />
                        </CheckboxDiv>
                      </Grid.Column>
                      <Grid.Column width={6} style={{paddingLeft: '0rem'}}>
                        {this.foundDuplicatesSection(values.includeInactive, form)}
                      </Grid.Column>
                      <Grid.Column width={7}>
                        <Button positive
                                compact
                                type="button"
                                floated={"right"}
                                style={{marginRight: ".5rem"}}
                                size="tiny"
                                onClick={() => this.copyInsuranceAction(values)}
                                disabled={copyInsurancesDisabled}
                        >
                          {getMessage("button.copyInsurance")}
                        </Button>
                        <Button positive
                                compact
                                type="button"
                                floated={"right"}
                                style={{marginRight: ".5rem"}}
                                size="tiny"
                                onClick={() => this.mergePatientsAction(values)}
                                disabled={mergeDisabled}
                        >
                          {getMessage("button.merge")}
                        </Button>
                        <Button compact
                                type="button"
                                floated={"right"}
                                style={{marginRight: ".5rem"}}
                                size="tiny"
                                secondary
                                onClick={() => this.resetSearchPatients(form)}
                                disabled={submitting}
                        >
                          {getMessage("button.reset")}
                        </Button>
                        <Button compact
                                type="button"
                                floated={"right"}
                                style={{marginRight: ".5rem"}}
                                size="tiny"
                                primary
                                onClick={() => this.patientMergingSearch(values)}
                                disabled={submitting}
                        >
                          {getMessage("search")}
                        </Button>
                        <Button negative
                                compact
                                type="button"
                                floated={"right"}
                                style={{marginRight: ".5rem"}}
                                size="tiny"
                                onClick={() => this.showModal()}
                                disabled={submitting}
                        >
                          {getMessage("patient.merging.find")}
                        </Button>
                      </Grid.Column>
                    </StyledButtonRow>
                  </React.Fragment>
                </PraxFormGrid>
              </SearchFormLeftDiv>
            </ReactHotkeys>
          </FormDiv>
          {this.renderUpsertFoundModal(form)}
          <SearchResultComponent numResults={numberOfPatients} textMessage={"search.resultsSummary"}/>
        </React.Fragment>
    );
  };

  nextDuplicatePatient = (index: number) => {
    const {duplicatePatients} = this.state;
    if (duplicatePatients && duplicatePatients.patientDuplicateData
        && index !== duplicatePatients.patientDuplicateData.length - 1) {
      this.setState({
        selectedDuplicatePatientIx: index + 1
      })
    }
  }

  previousDuplicatePatient = (index: number) => {
    const {duplicatePatients} = this.state;
    if (duplicatePatients && duplicatePatients.patientDuplicateData && index !== 0) {
      this.setState({
        selectedDuplicatePatientIx: index - 1
      })
    }
  }

  searchDuplicatePatientFromList = (values: any, includeInactive: boolean, form: FormApi) => {
    this.setState({patientsLoaded: false});
    if (values) {
      const birthDateMoment = moment(values.birthDate);
      const request: PatientMergingSearchRequest = {
        name: values.name,
        firstName: values.firstName,
        birthDate: birthDateMoment.isValid() ? birthDateMoment.format("DD.MM.YYYY") : "",
        includeInactive: includeInactive
      };
      form.reset(request)
      this.patientMergingSearch(request)
    }
    this.setState({patientsLoaded: true});
  }

  foundDuplicatesSection = (includeInactive: boolean, form: FormApi) => {
    const {getMessage} = this.props;
    const {duplicatePatients, selectedDuplicatePatientIx} = this.state;
    if (duplicatePatients && duplicatePatients.patientDuplicateData && selectedDuplicatePatientIx !== undefined) {

      const total = duplicatePatients.patientDuplicateData.length;
      const duplicatePatient = duplicatePatients.patientDuplicateData[selectedDuplicatePatientIx];
      const patientBirthDate = moment(duplicatePatient.birthDate).format("DD.MM.YYYY")

      return (
          <PxGrid>
            {duplicatePatient &&
            <Grid.Row>
              <Grid.Column width={2} verticalAlign={'middle'}>
                <Button compact
                        type="button"
                        style={{marginRight: ".5rem"}}
                        size="tiny"
                        secondary
                        onClick={() => this.previousDuplicatePatient(selectedDuplicatePatientIx)}
                        disabled={selectedDuplicatePatientIx === 0}
                >
                  {getMessage("patientCentral.patientEntry.actions.back")}
                </Button>
              </Grid.Column>
              <Grid.Column width={2} verticalAlign={'middle'}>
                <strong>{selectedDuplicatePatientIx + 1}/{total}</strong>
              </Grid.Column>
              <Grid.Column width={6} verticalAlign={'middle'}>
                {duplicatePatient.firstName}, {duplicatePatient.name}, {patientBirthDate}&nbsp;
                (<strong>{duplicatePatient.count}</strong>)
              </Grid.Column>
              <Grid.Column width={6} verticalAlign={'middle'}>
                <Button compact
                        type="button"
                        style={{marginRight: ".5rem"}}
                        size="tiny"
                        secondary
                        onClick={() => this.nextDuplicatePatient(selectedDuplicatePatientIx)}
                        disabled={selectedDuplicatePatientIx === duplicatePatients.patientDuplicateData.length - 1}
                >
                  {getMessage("patientCentral.patientEntry.actions.next")}
                </Button>
                <Button compact
                        type="button"
                        size="tiny"
                        primary
                        onClick={() => this.searchDuplicatePatientFromList(duplicatePatient, includeInactive, form)}
                >
                  {getMessage("button.use")}
                </Button>
              </Grid.Column>
            </Grid.Row>
            }
          </PxGrid>
      )
    }
  }

  rowGetter = ({index}: any) => {
    const {foundPatientMergingInfos} = this.state;

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {
      return foundPatientMergingInfos.patientMergeData[index];
    }
  };

  foundPatientRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};
    const {foundPatientMergingInfos, selectedPatient} = this.state;

    let styles = {
      ...style,
      ...rowStyle
    };

    if (selectedPatient && foundPatientMergingInfos && selectedPatient.patientId
        == foundPatientMergingInfos.patientMergeData[index].patientId) {
      styles = {
        ...style,
        ...selectedRow
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.markPatient(index)}
        >
          {columns}
        </div>
    );
  };

  birthDateCellRenderer = ({rowData}: any) => {
    if (moment(rowData.patientResponse.birthDate).isValid()) {
      return (
          <div>{moment(rowData.patientResponse.birthDate).format("DD.MM.YYYY")}</div>
      );
    }
    return "";
  };

  treatmentsAndBillsCellRenderer = ({rowIndex}: any) => {

    const {foundPatientMergingInfos} = this.state;
    const {getMessage} = this.props;

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {

      const treatments = foundPatientMergingInfos.patientMergeData[rowIndex].treatments;

      const openBills = foundPatientMergingInfos.patientMergeData[rowIndex].numberOfOpenBills;

      const stornoBills = foundPatientMergingInfos.patientMergeData[rowIndex].numberOfStornoBills;

      const payedBills = foundPatientMergingInfos.patientMergeData[rowIndex].numberOfPaidBills;

      let summary = [];

      if (treatments.length > 0) {
        summary.push(<div key="treatments">{`${treatments.length} ${getMessage("treatments")}`}</div>);
      }

      if (openBills > 0) {
        summary.push(<div key="openBills"><span>{`${openBills} ${getMessage("billJournal.openBills")}`}</span></div>);
      }

      if (stornoBills > 0) {
        summary.push(<div key="stornoBills">{`${stornoBills} ${getMessage("stornoType.BILL")}`}</div>);
      }

      if (payedBills > 0) {
        summary.push(<div key="payedBills">{`${payedBills} ${getMessage("billJournal.paidBills")}`}</div>);
      }

      return summary;
    }

    return "";

  };

  vaccinationsAndTestResultRenderer = ({rowIndex}: any) => {

    const {foundPatientMergingInfos} = this.state;
    const {getMessage} = this.props;

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {

      const vaccinations = foundPatientMergingInfos.patientMergeData[rowIndex].vaccinations;

      const testResults = foundPatientMergingInfos.patientMergeData[rowIndex].testResults;

      const summary = [];

      if (vaccinations.length > 0) {
        summary.push(<div
            key="vaccination">{`${vaccinations.length} ${getMessage("patient.search.vaccination")}`}</div>);
      }

      if (testResults.length > 0) {
        summary.push(<div key="testResults">{`${testResults.length} ${getMessage("patient.search.testResult")}`}</div>)
      }

      return summary;

    }

    return "";

  };

  addressCellRenderer = ({rowData}: any) => {
    const street = rowData.patientResponse.street ? `${rowData.patientResponse.street}` : "";
    const zip = rowData.patientResponse.zip ? `, ${rowData.patientResponse.zip}` : "";
    const city = rowData.patientResponse.city ? `, ${rowData.patientResponse.city}` : "";

    return `${street}${zip}${city}`;
  };

  selectMergePatient = (patientId: number, index: number) => {
    const {foundPatientMergingInfos} = this.state;

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {

      let patientMergeData = foundPatientMergingInfos.patientMergeData[index];

      if (patientMergeData) {
        patientMergeData.mergeSelection = !patientMergeData.mergeSelection;

        if (patientMergeData.mergeSelection) {
          // in case we do select new check if masterMergingSelection should be set to true
          const notAllEnabledAreSelected = foundPatientMergingInfos.patientMergeData
              .filter(patientData => !patientData.mergeSelection && !patientData.disableMerging).length;
          if (notAllEnabledAreSelected == 0) {
            this.setState({
              masterMergeFlag: true
            })
          }
        }
      }
    }
  }

  selectMasterPatient = (patientId: number, index: number) => {
    const {foundPatientMergingInfos, masterPatientId} = this.state;

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {

      let selectedPatientMergeData = foundPatientMergingInfos.patientMergeData[index];

      if (selectedPatientMergeData.masterSelection) {
        // previous state is selected, make deselection
        selectedPatientMergeData.masterSelection = false;

        // enable all other indexes (no more container limitation)
        foundPatientMergingInfos.patientMergeData.forEach(patientMD => {
          patientMD.disableMerging = false;
        })
        this.setState({
          masterPatientId: undefined,
          masterPatientContainer: undefined
        })
      } else {
        // previous state is not selected, make selection
        const masterPatId = selectedPatientMergeData.patientId;
        const masterCompDesc = selectedPatientMergeData.patientResponse.companyDescription;
        const compContainer = selectedPatientMergeData.patientResponse.companyContainer;
        selectedPatientMergeData.masterSelection = true;
        selectedPatientMergeData.disableMerging = true;
        // previous state is not selected make disable/enable/status bases on the container
        foundPatientMergingInfos.patientMergeData.forEach(patientMD => {

          if (patientMD.patientId !== masterPatId) {
            // all others if not same container disable them and remove selection
            // disable validation of company container because of copy insurance, do validation on the backend
            if (false && ((compContainer && compContainer !== patientMD.patientResponse.companyContainer)
                || (!compContainer && patientMD.patientResponse.companyContainer)
                || (masterCompDesc && masterCompDesc !== patientMD.patientResponse.companyDescription)
                || (!masterCompDesc && patientMD.patientResponse.companyDescription))
            ) {
              patientMD.mergeSelection = false;
              patientMD.disableMerging = true;
            } else {
              patientMD.disableMerging = false;
            }
          }
        })

        this.setState({
          masterPatientId: masterPatId,
          masterPatientContainer: selectedPatientMergeData.patientResponse.companyContainer
        })
      }
    }
  }

  mergeSelectCellRenderer = (data: any) => {
    const {rowIndex, rowData, cellData} = data;
    const {masterPatientId, masterPatientContainer} = this.state;

    let disabled = rowData.disableMerging;

    return (
        <CheckboxSc
            checked={cellData}
            onChange={() => this.selectMergePatient(rowData.id, rowIndex)}
            disabled={disabled}
        />
    )
  };

  masterSelectCellRenderer = (data: any) => {
    const {rowIndex, rowData, cellData} = data;
    const {masterPatientId} = this.state;

    let disabled = (masterPatientId && masterPatientId !== rowData.patientId) || rowData.mergeSelection;

    return (
        <CheckboxSc
            checked={cellData}
            onChange={() => this.selectMasterPatient(rowData.id, rowIndex)}
            disabled={disabled}
        />
    )

  };

  patientIdCellRendered = ({rowData}: any) => {
    const {getMessage} = this.props;
    const {mergeResponse} = this.state;
    const patientId = rowData.patientId;

    let error = null;
    if (mergeResponse && mergeResponse.errors && mergeResponse.errors.length) {
      error = mergeResponse.errors.find(e => e.mergingPatientId === patientId);
    }

    return (
        <StyledCell>
          {error &&
          <Icon name={'warning sign'} color={'red'} title={getMessage(error.errorKey)}/>
          }
          {patientId}
          <Icon
              name={'edit outline'}
              title={getMessage("patient.search.table.editPatient.title")}
              onClick={() => this.openEditPatientModal(patientId)}
              className="edit-patient-icon"
          />
        </StyledCell>
    );
  }

  nameCellRenderer = ({rowData}: any) => {
    const name = rowData.patientResponse.name;
    if (name) {
      return (`${name}`);
    } else {
      return "";
    }
  }

  firstNameCellRenderer = ({rowData}: any) => {
    const firstName = rowData.patientResponse.firstName;
    if (firstName) {
      return (`${firstName}`);
    } else {
      return "";
    }
  }

  ahvNumberCellRenderer = ({rowData}: any) => {
    const ahvNumber = rowData.patientResponse.ahvNumber;
    if (ahvNumber) {
      return (ahvNumber);
    } else {
      return "";
    }
  }

  insuranceCellRenderer = ({rowData}: any) => {
    const insuranceDescription = rowData.patientResponse.insuranceDescription;
    if (insuranceDescription) {
      return (`${rowData.patientResponse.insuranceDescription}`);
    } else {
      return "";
    }
  }

  companyCellRenderer = ({rowData}: any) => {
    const companyDescription = rowData.patientResponse.companyDescription
        ? rowData.patientResponse.companyDescription
        : "";
    const companyContainer = rowData.patientResponse.companyContainer
        ? `/${rowData.patientResponse.companyContainer}`
        : "";

    return `${companyDescription}${companyContainer}`
  }

  emailCellRenderer = ({rowData}: any) => {
    const companyEmail = rowData.patientResponse.companyEmail;
    if (companyEmail) {
      return (`${companyEmail}`)
    } else {
      return "";
    }
  }

  statusCellRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;

    if (rowData.patientResponse.active) {
      return getMessage("active");
    }
    return getMessage("inactive")
  }

  patientMergeSelectHeader = () => {

    const {foundPatientMergingInfos, masterPatientId, masterPatientContainer, masterMergeFlag} = this.state;
    const {getMessage} = this.props;

    if (foundPatientMergingInfos && foundPatientMergingInfos.patientMergeData) {
      const countSelectedPatients = foundPatientMergingInfos.patientMergeData
          .filter(patientData => patientData.mergeSelection).length;

      const notAllEnabledAreSelected = foundPatientMergingInfos.patientMergeData
          .filter(patientData => !patientData.mergeSelection && !patientData.disableMerging).length;

      const indeterminate = notAllEnabledAreSelected > 0;

      return (
          <CheckboxSc
              checked={masterMergeFlag}
              //indeterminate={indeterminate}
              label={getMessage('patient.merging.mergeSelection') + " " + countSelectedPatients}
              disabled={false}
              onChange={
                () => {
                  // not selected select all enable one
                  foundPatientMergingInfos.patientMergeData.forEach(patientMF => {
                    if (!patientMF.disableMerging) {
                      patientMF.mergeSelection = !masterMergeFlag;
                    }
                  })

                  this.setState({
                    masterMergeFlag: !masterMergeFlag
                  })
                }
              }
          />
      )
    }

    return (
        <CheckboxSc
            label={getMessage('patient.merging.mergeSelection')}
            disabled={true}
        />

    )
  }

  renderPatientSegment = () => {

    const {foundPatientMergingInfos, selectedPatientIx} = this.state;
    const {getMessage} = this.props;

    return (

        <div id="patient-search-table-container"
             className="patients-search-container"
             tabIndex={0}
             onKeyDown={(event) => this.changeSelectedPatient(event)}
        >
          <StyleTable
              rowCount={foundPatientMergingInfos ? foundPatientMergingInfos.patientMergeData.length : 0}
              rowGetter={({index}: any) => foundPatientMergingInfos ? foundPatientMergingInfos.patientMergeData[index] : undefined}
              rowRenderer={this.foundPatientRowRenderer}
              rowHeight={80}
              scrollToIndex={selectedPatientIx ? selectedPatientIx : 0}
              columns={[
                {
                  width: 100,
                  label: getMessage('patient.search.table.id'),
                  dataKey: "patientId",
                  flexShrink: 0,
                  cellRenderer: this.patientIdCellRendered
                },
                {
                  width: 100,
                  label: getMessage('status'),
                  dataKey: "patientId",
                  cellRenderer: this.statusCellRenderer
                },
                {
                  width: 125,
                  label: getMessage('patient.search.table.name'),
                  dataKey: "patientId",
                  cellRenderer: this.nameCellRenderer
                },
                {
                  width: 125,
                  label: getMessage('patient.search.table.firstname'),
                  dataKey: "patientId",
                  cellRenderer: this.firstNameCellRenderer
                },
                {
                  width: 125,
                  label: getMessage('patient.search.table.birthdate'),
                  dataKey: "patientId",
                  cellRenderer: this.birthDateCellRenderer
                },
                {
                  width: 300,
                  label: getMessage('patient.search.table.address'),
                  dataKey: "patientId",
                  cellRenderer: this.addressCellRenderer
                },
                {
                  width: 150,
                  label: getMessage('patient.search.table.ahvNumber'),
                  dataKey: "patientId",
                  cellRenderer: this.ahvNumberCellRenderer
                },
                {
                  width: 300,
                  label: getMessage('patient.search.table.insurance'),
                  dataKey: "patientId",
                  cellRenderer: this.insuranceCellRenderer
                },
                {
                  width: 200,
                  label: getMessage('patient.search.table.company'),
                  dataKey: "patientId",
                  cellRenderer: this.companyCellRenderer
                },
                {
                  width: 200,
                  label: getMessage("email"),
                  dataKey: "patientId",
                  cellRenderer: this.emailCellRenderer
                },
                {
                  width: 200,
                  label: getMessage('treatments'),
                  dataKey: "patientId",
                  cellRenderer: this.treatmentsAndBillsCellRenderer
                },
                {
                  width: 200,
                  label: getMessage('patient.search.vaccination'),
                  dataKey: "patientId",
                  cellRenderer: this.vaccinationsAndTestResultRenderer
                },
                {
                  width: 150,
                  label: getMessage('patient.merging.masterSelection'),
                  dataKey: "masterSelection",
                  cellRenderer: this.masterSelectCellRenderer
                },
                {
                  width: 150,
                  label: this.patientMergeSelectHeader(),
                  dataKey: "mergeSelection",
                  cellRenderer: this.mergeSelectCellRenderer
                }
              ]}
          />
        </div>
    )
  };

  rowTreatmentCount = () => {
    const {selectedPatient} = this.state;
    let length = 0;
    if (selectedPatient && selectedPatient.treatments) {
      length = selectedPatient.treatments.length;
    }
    return length;
  }

  markTreatment = (treatmentIndex: number) => {

    const {selectedPatient} = this.state;
    if (selectedPatient) {
      this.setState({
        selectedTreatment: selectedPatient.treatments[treatmentIndex]
      })
    }

  }

  foundTreatmentRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};
    const {selectedPatient, selectedTreatment} = this.state;

    let styles = {
      ...style,
      ...rowStyle
    };

    if (selectedPatient && selectedTreatment && selectedPatient.treatments[index].id
        == selectedTreatment.id) {
      styles = {
        ...style,
        ...selectedRow
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.markTreatment(index)}
        >
          {columns}
        </div>
    );
  };

  dateCellRenderer = ({cellData}: any) => {

    return isoToDisplayDate(cellData);
  };

  dateTimeCellRenderer = ({cellData}: any) => {

    return isoToDisplayDateTime(cellData);
  }

  renderTreatmentSegment = () => {

    const {selectedPatient} = this.state;
    const {getMessage} = this.props;

    return (

        <StyleTable
            rowCount={this.rowTreatmentCount()}
            rowGetter={({index}: any) => selectedPatient ? selectedPatient.treatments[index] : undefined}
            rowRenderer={this.foundTreatmentRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: getMessage("bill.type.normal"),
                dataKey: "id",
                cellRenderer: (this.renderTreatmentBillStatus)
              },
              {
                width: 100,
                label: getMessage("bill.type.treatmentId"),
                dataKey: "id",
                flexShrink: 0,
                cellRenderer: this.renderTreatmentId
              },
              {
                width: 150,
                label: getMessage("bill.treatmentDate"),
                dataKey: "treatmentDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 150,
                label: getMessage("tariff"),
                dataKey: "tariff"
              }
            ]}
        />

    )

  }

  rowBillsCount = () => {
    const {selectedPatient, selectedTreatment} = this.state;
    let length = 0;

    if (selectedPatient && selectedTreatment && selectedPatient.bills) {
      const treatmentId = selectedTreatment.id;
      length = selectedPatient.bills.filter(bill => bill.treatmentId === treatmentId).length;
    }
    return length;
  }

  foundBillsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};

    let styles = {
      ...style,
      ...rowStyle
    };

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    )

  }

  rowBillsGetter = ({index}: any) => {

    const {selectedPatient, selectedTreatment} = this.state;

    let bills: BillsMergingInfo[] = [];

    if (!!(selectedTreatment && selectedPatient)) {
      const treatmentId = selectedTreatment.id;
      bills = selectedPatient.bills.filter(bill => bill.treatmentId === treatmentId);
    }

    return bills[index];

  }

  getBillIcon = ({rowData}: any) => {

    return <div>{getBillIcon(rowData)}</div>

  };

  billHashRenderer = ({cellData}: any): React.ReactNode => {
    return (
        <StyledBillCell>
          {cellData}
        </StyledBillCell>
    );
  }

  paidAmountCellRenderer = ({rowData}: any) => {
    return <StyledBillCell>{numberFormatter.format(rowData.paidAmount)}</StyledBillCell>
  };

  renderBillsSegment = () => {

    const {getMessage} = this.props;

    return (

        <StyleTable
            rowCount={this.rowBillsCount()}
            rowGetter={this.rowBillsGetter}
            rowRenderer={this.foundBillsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: getMessage("bill.type.normal"),
                dataKey: "id",
                cellRenderer: this.getBillIcon
              },
              {
                width: 200,
                label: getMessage("bill.date"),
                dataKey: "billDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 150,
                label: getMessage("#"),
                dataKey: "id",
                cellRenderer: this.billHashRenderer
              },
              {
                width: 150,
                label: getMessage("tariffTaxPoint.procedureGroups.value"),
                dataKey: "amount",
                cellRenderer: (data: any) => this.paidAmountCellRenderer(data)
              },
              {
                width: 200,
                label: getMessage("bill.paymentDate"),
                dataKey: "paymentDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 200,
                label: getMessage("bill.openAmount"),
                dataKey: "openAmount",
                cellRenderer: (data: any) => this.paidAmountCellRenderer(data)
              }
            ]}
        />

    )

  }

  renderTreatmentBillStatus = ({rowData}: any): React.ReactNode => {

    if (BillStatus.BILL == rowData.billStatus) {
      return <Icon name={"file alternate outline"}/>;
    } else if (BillStatus.INTERMEDIATE_BILL == rowData.billStatus) {
      return <Icon name={"file alternate"}/>
    } else if (rowData.status) {
      return <Icon name={"key"}/>
    }

    return <Fragment/>

  }

  renderTreatmentId = ({cellData, rowData}: any): React.ReactNode => {
    const {getMessage, currentUser} = this.props;
    const {selectedTreatment} = this.state;
    const treatmentId = cellData;

    const shouldDisableButtons = (BillStatus.OPEN != rowData.billStatus)
        || (currentUser
            && currentUser.userConfig.mandateId !== rowData.mandateId);

    const editIconName = shouldDisableButtons ? 'info circle' : 'edit outline';

    return (
        <StyledCell>
          {treatmentId}
          <Icon
              name={editIconName}
              title={getMessage("bill.editTreatment.icon.title")}
              onClick={() => {
                if (shouldDisableButtons) {
                  this.openEditTreatmentModal(treatmentId, TreatmentMode.DETAILS)
                } else {
                  this.openEditTreatmentModal(treatmentId)
                }
              }}
              className="edit-treatment-icon"
              disabled={!selectedTreatment}
          />
        </StyledCell>
    );
  }

  rowVaccinationCount = () => {
    const {selectedPatient} = this.state;
    let length = 0;
    if (selectedPatient && selectedPatient.vaccinations) {
      length = selectedPatient.vaccinations.length;
    }

    return length;
  }

  foundVaccinationsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};

    let styles = {
      ...style,
      ...rowStyle
    };

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    )

  }

  renderVaccinationSegment = () => {

    const {selectedPatient} = this.state;
    const {getMessage} = this.props;

    return (

        <StyleTable
            rowCount={this.rowVaccinationCount()}
            rowGetter={({index}: any) => selectedPatient ? selectedPatient.vaccinations[index] : undefined}
            rowRenderer={this.foundVaccinationsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 200,
                label: getMessage("patientRecord.vaccination.medicament"),
                dataKey: "medicamentDescription"
              },
              {
                width: 100,
                label: getMessage("patientRecord.vaccination.lotNumber"),
                dataKey: "lotNumber"
              },
              {
                width: 100,
                label: getMessage("patientRecord.vaccination.dosis"),
                dataKey: "dosis"
              },
              {
                width: 200,
                label: getMessage("date"),
                dataKey: "date",
                cellRenderer: (this.dateCellRenderer)
              }
            ]}
        />
    )

  }

  rowTestResultCount = () => {
    const {selectedPatient} = this.state;
    let length = 0;
    if (selectedPatient && selectedPatient.testResults) {
      length = selectedPatient.testResults.length;
    }
    return length;
  }

  foundTestResultRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};

    let styles = {
      ...style,
      ...rowStyle
    };

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.markTreatment(index)}
        >
          {columns}
        </div>
    );
  };

  testResult = ({rowData}: any) => {

    const {getMessage} = this.props;

    return (getMessage(`enum.testResult.${rowData.testResult}`))

  }

  renderCertificateIcon = ({rowData}: any) => {

    const {getMessage} = this.props;

    const isRevoked = rowData.resultStatus === TestResultStatus.REVOKED

    return (
        <div>
          {getMessage(`enum.InterventionExternalCode.${rowData.externalCode}`)}
          <Icon className={"resultStatusIcon"}
                color={isRevoked ? "grey" : "blue"}
                title={isRevoked ? "revoked" : "valid"}
                name="certificate"/>
        </div>
    )

  }

  renderTestResultSegment = () => {

    const {selectedPatient} = this.state;
    const {getMessage} = this.props;

    return (

        <StyleTable
            rowCount={this.rowTestResultCount()}
            rowGetter={({index}: any) => selectedPatient ? selectedPatient.testResults[index] : undefined}
            rowRenderer={this.foundTestResultRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 200,
                label: getMessage("patientRecord.testResult.externalCode"),
                dataKey: "resultStatus",
                cellRenderer: (this.renderCertificateIcon)
              },
              {
                width: 250,
                label: getMessage("patientRecord.testResult.testKitCode"),
                dataKey: "testKitCode"
              },
              {
                width: 150,
                label: getMessage("patientRecord.testResult.testingDate"),
                dataKey: "testingDate",
                cellRenderer: (this.dateTimeCellRenderer)
              },
              {
                width: 150,
                label: getMessage("patientRecord.testResult.testResult"),
                dataKey: "testResult",
                cellRenderer: (this.testResult)
              },
              {
                width: 150,
                label: getMessage("patientRecord.testResult.resultDate"),
                dataKey: "resultDate",
                cellRenderer: (this.dateTimeCellRenderer)
              }
            ]}
        />
    )
  }

  setDuplicatePatientIx = (index?: number) => {
    this.setState({
      selectedDuplicatePatientIx: index
    })
  }

  renderUpsertFoundModal = (form: FormApi): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, duplicatePatients} = this.state;

    return (
        <StyledModal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("patient.merging.foundPatients")}</Modal.Header>
          <FormModalContentContainer>
            <DuplicatePatientsListModal
                duplicatePatients={duplicatePatients}
                onSave={(selectedPatient: PatientDuplicateResponse) => this.onModalClosing(selectedPatient, form)}
                onCancel={this.hideModal}
                setDuplicatePatientIx={this.setDuplicatePatientIx}
            />
          </FormModalContentContainer>
        </StyledModal>
    )

  }

  showMergeResponseMessage = (mergeResponse?: Partial<PatientMergeResponse>) => {

    const {getMessage, getMessageWithNamedParams, selectionNotificationSystem} =  this.props;

    let generalErrorText = undefined;
    let successMessage = undefined;
    if (mergeResponse) {

      if (mergeResponse.errors && mergeResponse.errors.length > 0) {
        let generalErrorKey = "patient.merging.error.general"
        if (mergeResponse.copyMode) {
          generalErrorKey = "patient.merging.error.generalCopyInsurance"
        }
        generalErrorText = getMessageWithNamedParams(generalErrorKey, {numberOfErrors: mergeResponse.errors.length});
      }

      if (mergeResponse.generalError) {
        if (generalErrorText) {
          generalErrorText = generalErrorText + "<br/>" + mergeResponse.generalError;
        } else {
          generalErrorText = mergeResponse.generalError;
        }
      }

      if (mergeResponse.copyMode) {
        let successKey = "patient.merging.copyInsuranceSuccess";
        if (mergeResponse.numberOfCopiedInsurances == 0 && mergeResponse.numberOfUpdatedPatients == 0) {
          successKey = "patient.merging.nothingToCopyInsuranceSuccess"
        }
        successMessage = getMessageWithNamedParams(successKey,
            {
              patientsUpdated: mergeResponse.numberOfUpdatedPatients,
              insertedInsurances: mergeResponse.numberOfCopiedInsurances
            });
      } else {
        successMessage = getMessageWithNamedParams("patient.merging.mergeSuccess",
            {
              numberOfMergedPatients: mergeResponse.numberOfMergedPatients,
              masterPatientId: mergeResponse.masterPatientId,
              mergedPatientIds: mergeResponse.mergedPatientIds
            });
      }
    }

  //@ts-ignore
  const notification = selectionNotificationSystem.current;

  if (notification) {
    if (generalErrorText) {
      notification.addNotification({
        title: getMessage("error.header"),
        message: generalErrorText,
        level: 'error'
      });
    } else if (!generalErrorText && mergeResponse) {
      notification.addNotification({
        title: getMessage("patient.merging.mergeSuccess.title"),
        message: successMessage,
        level: 'success'
      });
    }
  }
}

}

export default withMessages(withAuthContext(withConsultationsContext(withMandateContext(withNotificationSystemContext(PatientMergingView)))));
